import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { addDays, addMonths, addWeeks, format, parseISO, startOfMonth } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Filter, MinusCircle } from 'lucide-react'
import { InputDate } from '../form'
import {
  PRESETS_PERIODOS,
  rotuloResumoSeletorPeriodo,
  type TipoPeriodoPreset,
} from './resolverDatasPeriodo'

export interface EstadoRascunhoPeriodo {
  tipo: TipoPeriodoPreset
  dataReferencia: string
  intervaloInicio: string
  intervaloFim: string
}

export interface SeletorPeriodoProps {
  tipoPeriodo: TipoPeriodoPreset
  onTipoPeriodoChange: (tipo: TipoPeriodoPreset) => void
  dataReferencia: string
  onDataReferenciaChange: (iso: string) => void
  intervaloInicio: string
  intervaloFim: string
  onIntervaloInicioChange: (iso: string) => void
  onIntervaloFimChange: (iso: string) => void
  /** Texto opcional ao lado do rótulo (ex.: qual critério de data a API usa). */
  descricaoAjuda?: string
  /** Preset usado pelo botão "Limpar filtros" dentro da modal. */
  presetPadraoAoLimpar?: TipoPeriodoPreset
  /** Chamado ao confirmar «Aplicar» na modal, após propagar tipo e datas ao pai (ex.: disparar busca). */
  onAplicarConfirmado?: () => void
}

const CHIP_BASE =
  'px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors duration-150'
const CHIP_IDLE = 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300'
const CHIP_SEL = 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500'

/**
 * Estado colapsado + modal compacta para escolha de período (presets, dia/semana/mês, intervalo).
 * Mudanças só vão ao formulário ao confirmar **Aplicar**.
 */
export default function SeletorPeriodo({
  tipoPeriodo,
  onTipoPeriodoChange,
  dataReferencia,
  onDataReferenciaChange,
  intervaloInicio,
  intervaloFim,
  onIntervaloInicioChange,
  onIntervaloFimChange,
  descricaoAjuda,
  presetPadraoAoLimpar = 'ultimoAno',
  onAplicarConfirmado,
}: SeletorPeriodoProps) {
  const resumoLinha = rotuloResumoSeletorPeriodo(
    tipoPeriodo,
    dataReferencia,
    intervaloInicio,
    intervaloFim,
  )

  const [modalAberta, setModalAberta] = useState(false)
  const [rascunho, setRascunho] = useState<EstadoRascunhoPeriodo>(() => ({
    tipo: tipoPeriodo,
    dataReferencia,
    intervaloInicio,
    intervaloFim,
  }))

  const sincronizarRascunhoDaProps = useCallback(() => {
    setRascunho({
      tipo: tipoPeriodo,
      dataReferencia,
      intervaloInicio,
      intervaloFim,
    })
  }, [tipoPeriodo, dataReferencia, intervaloInicio, intervaloFim])

  const fecharModal = useCallback(() => {
    setModalAberta(false)
  }, [])

  const abrirModal = () => {
    sincronizarRascunhoDaProps()
    setModalAberta(true)
  }

  const aplicar = () => {
    onTipoPeriodoChange(rascunho.tipo)
    onDataReferenciaChange(rascunho.dataReferencia)
    if (rascunho.tipo === 'intervalo')
    {
      onIntervaloInicioChange(rascunho.intervaloInicio)
      onIntervaloFimChange(rascunho.intervaloFim)
    }
    else
    {
      onIntervaloInicioChange('')
      onIntervaloFimChange('')
    }
    fecharModal()
    onAplicarConfirmado?.()
  }

  const limparRascunho = () => {
    const hoje = format(new Date(), 'yyyy-MM-dd')
    setRascunho({
      tipo: presetPadraoAoLimpar,
      dataReferencia: hoje,
      intervaloInicio: '',
      intervaloFim: '',
    })
  }

  const aoMarcarTipo = (id: TipoPeriodoPreset) => {
    setRascunho((d) => {
      const novo: EstadoRascunhoPeriodo = { ...d, tipo: id }
      if (id === 'intervalo')
      {
        const hoje = format(new Date(), 'yyyy-MM-dd')
        const iniMes = format(startOfMonth(new Date()), 'yyyy-MM-dd')
        novo.intervaloInicio = d.intervaloInicio.trim() ? d.intervaloInicio : iniMes
        novo.intervaloFim = d.intervaloFim.trim() ? d.intervaloFim : hoje
      }
      return novo
    })
  }

  const refDraftParsed =
    /^(\d{4})-(\d{2})-(\d{2})$/.test(rascunho.dataReferencia)
      ? parseISO(rascunho.dataReferencia)
      : new Date()
  const labelMesCapitalizado = format(refDraftParsed, 'MMMM yyyy', { locale: ptBR })
  const labelMesCabecalho = labelMesCapitalizado.charAt(0).toUpperCase() + labelMesCapitalizado.slice(1)

  const stepReferencia = (direcao: 1 | -1) => {
    let d: Date
    if (rascunho.tipo === 'noMes')
      d = addMonths(refDraftParsed, direcao)
    else if (rascunho.tipo === 'naSemana')
      d = addWeeks(refDraftParsed, direcao)
    else
      d = addDays(refDraftParsed, direcao)
    setRascunho((prev) => ({ ...prev, dataReferencia: format(d, 'yyyy-MM-dd') }))
  }

  const mostrarNavegacao =
    rascunho.tipo === 'noDia' ||
    rascunho.tipo === 'naSemana' ||
    rascunho.tipo === 'noMes'

  useEffect(() => {
    if (!modalAberta)
      return undefined
    const aoTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape')
        fecharModal()
    }
    window.addEventListener('keydown', aoTecla)
    return () => window.removeEventListener('keydown', aoTecla)
  }, [modalAberta, fecharModal])

  const conteudoModal = (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-gray-100">
        <div
          title="Referência no calendário"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-500 bg-white text-sm font-semibold text-gray-900 shadow-theme-xs"
        >
          <CalendarDays className="w-4 h-4 text-brand-600 shrink-0" />
          <span>{labelMesCabecalho}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200">
            <Filter className="w-3.5 h-3.5" />
            Filtros
          </span>
          <button
            type="button"
            onClick={limparRascunho}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-brand-600"
          >
            <MinusCircle className="w-3.5 h-3.5" />
            Limpar filtros
          </button>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Período</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS_PERIODOS.map((op) => (
            <button
              key={op.id}
              type="button"
              onClick={() => aoMarcarTipo(op.id)}
              className={`${CHIP_BASE} ${rascunho.tipo === op.id ? CHIP_SEL : CHIP_IDLE}`}
            >
              {op.label}
            </button>
          ))}
        </div>
      </div>

      {mostrarNavegacao && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5 shadow-theme-xs">
          <button
            type="button"
            onClick={() => stepReferencia(-1)}
            className="p-1 rounded-md hover:bg-gray-100 text-gray-600"
            title="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="flex-1 text-center text-sm font-medium text-gray-800 tabular-nums min-w-[9rem]">
            {rascunho.tipo === 'noMes'
              ? labelMesCabecalho
              : format(refDraftParsed, 'dd/MM/yyyy', { locale: ptBR })}
          </span>
          <button
            type="button"
            onClick={() => stepReferencia(1)}
            className="p-1 rounded-md hover:bg-gray-100 text-gray-600"
            title="Próximo"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {rascunho.tipo === 'intervalo' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data início <span className="text-red-500">*</span>
            </label>
            <InputDate
              value={rascunho.intervaloInicio}
              onChange={(v) =>
                setRascunho((prev) => ({ ...prev, intervaloInicio: v }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm"
              showError={false}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data fim</label>
            <InputDate
              value={rascunho.intervaloFim}
              onChange={(v) =>
                setRascunho((prev) => ({ ...prev, intervaloFim: v }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm"
              showError={false}
            />
          </div>
        </div>
      )}
    </div>
  )

  return (
    <>
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={abrirModal}
          className="w-full flex items-center gap-3 justify-between rounded-xl border border-gray-200 bg-gray-50/90 px-4 py-3 text-left shadow-theme-sm hover:bg-gray-50 hover:border-gray-300 transition-colors"
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1.5 font-medium text-gray-800">
                <CalendarDays className="w-4 h-4 shrink-0 text-brand-600" />
                Período
              </span>
              {descricaoAjuda
                ? (
                  <span className="text-xs text-gray-500">{descricaoAjuda}</span>
                )
                : null}
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900 truncate">{resumoLinha}</div>
          </div>
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        </button>
      </div>

      {modalAberta &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999997] flex items-center justify-center bg-black/50 p-4"
            onClick={fecharModal}
          >
            <div
              className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto p-5"
              onClick={(e) => e.stopPropagation()}
            >
              {conteudoModal}
              <div className="flex flex-wrap items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 px-2"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={aplicar}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors shadow-theme-sm"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
